// Health Summary Card - Apple Health Style Hero Section
import { useEffect, useState } from 'react';
import { Card, Text } from '../ui';

/**
 * Animated Score Ring Component
 */
function ScoreRing({
    score,
    size = 140,
    strokeWidth = 10,
    color = '#10B981',
    bgColor = 'rgba(255,255,255,0.1)',
    label,
    animate = true
}) {
    const [animatedScore, setAnimatedScore] = useState(animate ? 0 : score);

    useEffect(() => {
        if (!animate) {
            setAnimatedScore(score);
            return;
        }

        const duration = 1000;
        const start = Date.now();
        const startValue = animatedScore;

        const timer = setInterval(() => {
            const elapsed = Date.now() - start;
            const progress = Math.min(elapsed / duration, 1);
            // Easing function
            const eased = 1 - Math.pow(1 - progress, 3);
            setAnimatedScore(Math.round(startValue + (score - startValue) * eased));

            if (progress >= 1) clearInterval(timer);
        }, 16);

        return () => clearInterval(timer);
    }, [score, animate]);

    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (animatedScore / 100) * circumference;

    return (
        <div className="relative inline-flex items-center justify-center">
            <svg width={size} height={size} className="transform -rotate-90">
                {/* Background circle */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={bgColor}
                    strokeWidth={strokeWidth}
                />
                {/* Progress circle */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    style={{ transition: animate ? 'stroke-dashoffset 0.1s ease-out' : 'none' }}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl sm:text-4xl font-bold text-white">{animatedScore}</span>
                {label && <span className="text-xs sm:text-sm text-white/70">{label}</span>}
            </div>
        </div>
    );
}

/**
 * Mini Score Ring for Quick Stats
 */
function MiniScoreRing({ score, icon, label, color }) {
    const size = 48;
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;

    return (
        <div className="flex flex-col items-center gap-1">
            <div className="relative">
                <svg width={size} height={size} className="transform -rotate-90">
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth={strokeWidth}
                    />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-lg">
                    {icon}
                </div>
            </div>
            <span className="text-xs text-white/70">{label}</span>
            <span className="text-sm font-semibold text-white">{score}</span>
        </div>
    );
}

/**
 * Constitution Badge Chip
 */
function ConstitutionBadge({ name, score, color }) {
    return (
        <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
            style={{ backgroundColor: `${color}20`, borderColor: `${color}40`, borderWidth: 1 }}
        >
            <span className="text-xs sm:text-sm font-medium text-white">{name}</span>
            <span
                className="text-xs font-bold px-1.5 py-0.5 rounded"
                style={{ backgroundColor: color, color: '#fff' }}
            >
                {score}%
            </span>
        </div>
    );
}

/**
 * Health Summary Card Component
 * Apple Health style hero section with animated rings
 */
export default function HealthSummaryCard({
    overallScore = 0,
    systemScores = {},
    primaryConstitution = null,
    secondaryConstitutions = [],
    timestamp = null,
    isMock = false,
    analysisMethod = 'AI'
}) {
    // Get score color based on value
    const getScoreColor = (score) => {
        if (score >= 80) return '#10B981'; // Green
        if (score >= 60) return '#F59E0B'; // Amber
        if (score >= 40) return '#F97316'; // Orange
        return '#EF4444'; // Red
    };

    // Get score label
    const getScoreLabel = (score) => {
        if (score >= 80) return '🌟 Sức khỏe tốt!';
        if (score >= 60) return '⚡ Cần cải thiện';
        if (score >= 40) return '⚠️ Cần chú ý';
        return '🚨 Cần can thiệp';
    };

    // Quick stats data
    const quickStats = [
        {
            id: 'cardiovascular',
            icon: '❤️',
            label: 'Tim mạch',
            score: systemScores.cardiovascular || 0,
            color: '#EF4444'
        },
        {
            id: 'nervous',
            icon: '🧠',
            label: 'Thần kinh',
            score: systemScores.nervous || 0,
            color: '#8B5CF6'
        },
        {
            id: 'metabolic',
            icon: '🔄',
            label: 'Chuyển hóa',
            score: systemScores.metabolic || 0,
            color: '#3B82F6'
        },
        {
            id: 'digestive',
            icon: '🫃',
            label: 'Tiêu hóa',
            score: systemScores.digestive || 0,
            color: '#F59E0B'
        }
    ];

    const formatDate = (date) => {
        if (!date) return new Date().toLocaleDateString('vi-VN');
        return new Date(date).toLocaleDateString('vi-VN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="rounded-3xl p-6 sm:p-8 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 shadow-xl overflow-hidden relative">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-secondary/20 to-transparent rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

            <div className="relative z-10">
                {/* Mock data badge */}
                {isMock && (
                    <div className="absolute top-0 right-0 px-3 py-1 bg-yellow-500/20 rounded-full border border-yellow-500/30">
                        <span className="text-yellow-400 text-xs font-bold">Dữ liệu mẫu</span>
                    </div>
                )}

                {/* Main Score Section */}
                <div className="text-center mb-6">
                    <Text className="text-white/70 text-sm sm:text-base font-medium mb-4 block">
                        Điểm sức khỏe tổng quát
                    </Text>

                    <div className="flex justify-center mb-4">
                        <ScoreRing
                            score={overallScore}
                            size={140}
                            strokeWidth={10}
                            color={getScoreColor(overallScore)}
                            label="/100"
                        />
                    </div>

                    <Text className="text-white/90 font-medium text-lg">
                        {getScoreLabel(overallScore)}
                    </Text>
                </div>

                {/* Quick Stats */}
                <div className="flex justify-center gap-4 sm:gap-6 mb-6 flex-wrap">
                    {quickStats.map(stat => (
                        <MiniScoreRing
                            key={stat.id}
                            icon={stat.icon}
                            label={stat.label}
                            score={stat.score}
                            color={stat.color}
                        />
                    ))}
                </div>

                {/* Constitution Badge */}
                {primaryConstitution && (
                    <div className="text-center mb-4">
                        <Text className="text-white/60 text-xs mb-2 block">Thể chất chủ đạo</Text>
                        <div className="flex flex-wrap justify-center gap-2">
                            <ConstitutionBadge
                                name={primaryConstitution.name}
                                score={primaryConstitution.score}
                                color={primaryConstitution.color}
                            />
                            {secondaryConstitutions.slice(0, 2).map((c, idx) => (
                                <ConstitutionBadge
                                    key={idx}
                                    name={c.name}
                                    score={c.score}
                                    color={c.color}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Timestamp & Meta */}
                <div className="text-center pt-4 border-t border-white/10">
                    <Text className="text-white/50 text-xs">
                        📅 {formatDate(timestamp)} • {analysisMethod} Analysis
                    </Text>
                </div>
            </div>
        </div>
    );
}
